import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { Tooltip } from '@liveauctioneers/hammer-ui-core/tooltip';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import TopBadge from '@/components/Badges/TopBadge';
import { RatingDisplaySmall } from '@/components/RatingDisplaySmall/RatingDisplaySmall';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { mergedCaterwaulAndHammerUIThemes } from '@/theme/mergedCaterwaulAndHammerUIThemes';
import { HouseData } from '@/types/HousePercentile';
import { Rating } from '@/types/Rating';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { truncateToOneDecimalWithoutRounding } from '@/utils/truncateToOneDecimalWithoutRounding';

import { Rankings } from './Rankings';
import RatingsCounter from './RatingsCounter';

const MINIMUM_REVIEW_COUNT_FOR_SUMMARY = 5;

export interface RatingsSummaryProps {
    houseRating: Rating;
    modal?: boolean;
    percentiles: HouseData;
    updateFilterFromBarChart: Function;
}

export const RatingsSummary = ({ houseRating, modal, percentiles, updateFilterFromBarChart }: RatingsSummaryProps) => {
    const formatMessage = useAppFormatMessage();
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = mergedCaterwaulAndHammerUIThemes;

    const mobile = useIsNarrowerThan(tabletNarrowWidthNum + 1);
    const isTopRated = Boolean(percentiles.isTopRated);
    const overall = houseRating?.overall ? truncateToOneDecimalWithoutRounding(houseRating.overall) : 0;
    const showRankings = houseRating.totalReviews >= 25;
    const houseRatingDisplay = houseRating.totalReviews >= 5 ? houseRating.overall : 0;

    return (
        <section
            className={cn(
                'mb-md flex items-center justify-evenly',
                'smMax:flex-wrap',
                'xsMax:flex-col xsMax:[&>*]:w-full'
            )}
        >
            <div className="flex h-[120px] w-1/5 flex-col">
                {isTopRated && (
                    <div className="mb-sm">
                        <TopBadge />
                    </div>
                )}
                <div>
                    <RatingDisplaySmall
                        hideReviewCount
                        rating={houseRatingDisplay}
                    />
                </div>
                <div className="mt-md flex">
                    {houseRating.totalReviews >= MINIMUM_REVIEW_COUNT_FOR_SUMMARY
                        ? formatMessage(['review', 'overall_rating_label'], {
                              values: {
                                  rating: overall,
                              },
                          })
                        : formatMessage(['review', 'not_enough_data'])}
                    {!modal && (
                        <>
                            <Info
                                className="mt-[1px] ml-2xs"
                                color="primary"
                                id="ratings-summary-info-trigger"
                                size="sm"
                                style="fill"
                            />
                            <Tooltip
                                anchorSelect="#ratings-summary-info-trigger"
                                place="bottom"
                            >
                                {formatMessage(['review', 'ratings_explanation'])}
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>
            <div className="ml-lg max-w-[360px]">
                <RatingsCounter
                    houseRating={houseRating}
                    updateFilter={updateFilterFromBarChart}
                />
            </div>
            <div className="mt-sm grow">
                {Boolean(showRankings) && (
                    <Rankings
                        mobile={mobile}
                        modal={modal}
                        percentiles={percentiles}
                    />
                )}
            </div>
        </section>
    );
};
